import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { RemoveProgramParticipantsDialogComponent } from 'app/library/course-offering-participants/components/dialogs/remove-program-participants-dialog/remove-program-participants-dialog.component';
import { IRemoveProgramParticipantsDialogResult } from 'app/library/course-offering-participants/components/dialogs/remove-program-participants-dialog/remove-program-participants-dialog-result.interface';
import { CourseOfferingParticipantsService } from 'app/library/course-offering-participants/services/course-offering-participants.service';
import { RemoveUsersFromModulesAsParticipants, RemoveUsersFromModulesAsParticipantsSuccess } from 'app/library/module-user/actions';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class UserModuleRemoveParticipantService {
    constructor(
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _store: Store,
        private _action$: Actions,
        private _courseOfferingParticipantsService: CourseOfferingParticipantsService
    ) {}

    removeAsParticipant(moduleUser: ModuleUserExtended, destroy$: Observable<unknown>, onSuccess: () => void): void {
        if (moduleUser.module.kind === ModuleKind.ProgramExecution) {
            this.removeFromProgramExecution(moduleUser, onSuccess);
            return;
        }

        this.removeFromModule(moduleUser, destroy$, onSuccess);
    }

    private removeFromModule(moduleUser: ModuleUserExtended, destroy$: Observable<unknown>, onSuccess: () => void): void {
        const dialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        dialogRef.componentInstance.confirmTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_PARTICIPANT';
        dialogRef.componentInstance.confirmButtonColor = 'warn';
        dialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.REMOVE_CONFIRMATION';
        dialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this._store.dispatch(
                    new RemoveUsersFromModulesAsParticipants({
                        userIds: [moduleUser.userId],
                        moduleIds: [moduleUser.moduleId],
                    })
                );
            }
        });

        this._action$.pipe(ofActionDispatched(RemoveUsersFromModulesAsParticipantsSuccess), takeUntil(destroy$)).subscribe(() => {
            dialogRef.close();
            onSuccess();
            this.showSuccessMessage();
        });
    }

    private async removeFromProgramExecution(moduleUser: ModuleUserExtended, onSuccess: () => void): Promise<void> {
        const userIds = [moduleUser.userId];
        const participantBookings = await this._courseOfferingParticipantsService.fetchParticipantBookings({
            userIds,
            programExecutionId: moduleUser.moduleId,
        });

        if (participantBookings === null) {
            return;
        }

        if (!participantBookings.length || !participantBookings[0].relevantExecutions?.length) {
            const success = await this._courseOfferingParticipantsService.unbookParticipant({
                bookings: [],
                userIds,
                removeFromProgram: true,
                programExecutionId: moduleUser.moduleId,
            });

            if (success) {
                onSuccess();
                this.showSuccessMessage();
            }

            return;
        }

        const dialogRef = this._matDialog.open(RemoveProgramParticipantsDialogComponent, {
            disableClose: false,
            width: '882px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = this._translateService.instant('FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_PARTICIPANT');
        dialogRef.componentInstance.program = moduleUser.module;
        dialogRef.componentInstance.participantBookings = participantBookings;

        dialogRef.afterClosed().subscribe(async (result: IRemoveProgramParticipantsDialogResult | false) => {
            if (!result || !result.bookings.length) {
                return;
            }

            const success = await this._courseOfferingParticipantsService.unbookParticipant({
                bookings: result.bookings,
                userIds,
                removeFromProgram: result.removeFromProgram,
                programExecutionId: moduleUser.moduleId,
            });

            if (success) {
                onSuccess();
                this.showSuccessMessage();
            }
        });
    }

    private showSuccessMessage(): void {
        const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

        this._matSnackBar.open(successMessage, 'OK', {
            verticalPosition: 'top',
            duration: SnackBarTime.medium,
        });
    }
}
